import type { BoCreateablePartner } from '@api/swagger/definitions/backoffice'
import { Global } from '@emotion/react'
import styled from '@emotion/styled'
import Button from '@mui/material/Button'
import Typography from '@mui/material/Typography'
import { useMemo, useState } from 'react'
import { type Control, type Path, useForm, useWatch } from 'react-hook-form'
import { useGermanDistrictCourts } from '../../api/react-query'
import { AutoCompleteController, TextFieldController } from '../../components/react-hook-components'
import Spacings from '../../figma/tokens/Spacings'
import FigmaBox from '../../mynt-components/components/FigmaBox'
import ModalForked from '../../mynt-components/components/ModalForked'
import { When } from '../../mynt-components/components/When'

export type CreateCustomerFormData<Country = string> = {
  companyName: string
  companyId: {
    regNo: string
    country: Country
  }
  templateId?: string
  cardPartner: BoCreateablePartner
}

type InternalCreateCustomerFormData = CreateCustomerFormData & {
  companyId: CreateCustomerFormData['companyId'] & {
    deCourtCode?: string
  }
}

type CreateCustomerModalProps = {
  onClose?: () => void
  onSubmit: (payload: CreateCustomerFormData) => void | Promise<void>
  isOpen: boolean | undefined
  countryOptions: { value: string; label: string }[]
  templateOptions: { value: string; label: string }[]
}

export const CreateCustomerModal = ({ onClose, onSubmit, isOpen, countryOptions, templateOptions }: CreateCustomerModalProps) => {
  const [isLoading, setIsLoading] = useState(false)
  const { data: germanDistrictCourts, isLoading: isLoadingDistrictCourts } = useGermanDistrictCourts()

  const deDistrictCourtOptions = useMemo(
    () => germanDistrictCourts?.map((court) => ({ value: court.code || '', label: court.name || '' })) || [],
    [germanDistrictCourts],
  )

  const partnerOptions = [
    { value: 'NONE', label: 'Mynt' },
    { value: 'AMPR', label: 'Ampr' },
  ]

  const form = useForm<InternalCreateCustomerFormData>({
    defaultValues: {
      companyName: '',
      companyId: {
        regNo: '',
        country: countryOptions[0]?.value || '',
        deCourtCode: '',
      },
      cardPartner: 'NONE',
    },
    mode: 'onChange',
  })

  const selectedCountry = useWatch({
    control: form.control,
    name: 'companyId.country',
  })

  const onFormSubmit = async (data: InternalCreateCustomerFormData) => {
    setIsLoading(true)

    let regNoFormatted = data.companyId.regNo
    if (data.companyId.country === 'DE' && data.companyId.deCourtCode) {
      regNoFormatted = `${data.companyId.regNo}-${data.companyId.deCourtCode}`
    }

    const formattedData: CreateCustomerFormData = {
      companyName: data.companyName,
      companyId: {
        regNo: regNoFormatted,
        country: data.companyId.country,
      },
      templateId: data.templateId,
      cardPartner: data.cardPartner,
    }

    const promiseOrVoid = onSubmit(formattedData)

    if (promiseOrVoid instanceof Promise) {
      return promiseOrVoid.finally(() => setIsLoading(false))
    }

    setIsLoading(false)
  }

  const handleClose = () => {
    onClose?.()
  }

  if (!isOpen) return null

  return (
    <ModalForked
      fullWidth
      alignHeadingWithCloseButton
      onClose={handleClose}
      heading={() => (
        <FigmaBox spacing={Spacings.large} left right>
          <Typography variant="h6">Create new customer</Typography>
        </FigmaBox>
      )}
    >
      <MuiSelectFontFix />
      <FigmaBox fullWidth spacing={Spacings.large} fullPadding gap={Spacings.min}>
        <Row data-testid="company-name" control={form.control} name="companyName" labelText="Company name" type="text" />
        <Row data-testid="reg-no" control={form.control} name="companyId.regNo" labelText="Reg. no" type="text" />
        <Row
          data-testid="template-select"
          control={form.control}
          name="templateId"
          labelText="Template"
          type="select"
          options={templateOptions}
        />
        <Row
          data-testid="card-partner-select"
          control={form.control}
          name="cardPartner"
          labelText="Partner"
          type="select"
          options={partnerOptions}
        />
        <Row
          data-testid="country-select"
          control={form.control}
          name="companyId.country"
          labelText="Country"
          type="select"
          options={countryOptions}
        />
        <When is={selectedCountry === 'DE'}>
          <Row
            data-testid="german-district-court-select"
            control={form.control}
            name="companyId.deCourtCode"
            labelText="District Court"
            type="select"
            options={deDistrictCourtOptions}
            disabled={isLoadingDistrictCourts}
          />
        </When>
        <FigmaBox top={Spacings.tiny} fullWidth>
          <Button
            loading={isLoading}
            onClick={form.handleSubmit(onFormSubmit)}
            variant="primary"
            disabled={!form.formState.isValid || isLoading}
          >
            Create customer
          </Button>
        </FigmaBox>
      </FigmaBox>
    </ModalForked>
  )
}

type Options = { value: string; label: string; disabled?: boolean }[]

type RowProps<T extends Record<string, any>> = {
  control: Control<T>
  labelText?: string
  name: Path<T>
  type?: 'text' | 'select' | 'number' | 'date'
  options?: Options
  adornmentText?: string
  onChange?: (event: any) => void
  disabled?: boolean
}

const Row = <T extends Record<string, any>>({
  control,
  labelText,
  name,
  type = 'number',
  options,
  adornmentText,
  disabled,
  ...props
}: RowProps<T>) => (
  <FigmaBox data-testid={props['data-testid']} fullWidth justify="space-between" direction="row">
    <FigmaBox fullWidth flex={2} style={{ whiteSpace: 'pre' }} justify="center">
      <Typography variant="subtitle1">{labelText}</Typography>
    </FigmaBox>
    <StyledTextFieldControllerContainer fullWidth flex={3}>
      <When is={['text', 'number'].includes(type)}>
        <TextFieldController
          disabled={disabled}
          type={type}
          noLabel
          name={name}
          control={control}
          adornment={adornmentText ? <Typography>{adornmentText}</Typography> : undefined}
        />
      </When>
      <When is={type === 'select'}>
        <AutoCompleteController control={control} name={name} options={options as Options} />
      </When>
    </StyledTextFieldControllerContainer>
  </FigmaBox>
)

// These fixes some issues that should not be applied everywhere that comes from UX. Would be improved once panda components are created
const MuiSelectFontFix = () => <Global styles={{ '.MuiPaper-root li': { fontSize: '14px !important' } }} />

const StyledTextFieldControllerContainer = styled(FigmaBox)`
  *:not(svg) {
    font-family: Inter !important;
    font-size: 14px;
  }

  input::-webkit-outer-spin-button,
  input::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  input[type='number'] {
    -moz-appearance: textfield;
  }
`
